import axios from "axios";
import { getStorageKey, setStorageKey, removeStorageKey } from "../utils/storage";

/**
 * Active-store state (Phase 5 of the multi-store access rework).
 *
 * Distinct from user.store_id_m (record ownership / home store — unchanged
 * everywhere else in this app): this is "which store am I CURRENTLY acting
 * as", sent as the X-Active-Store header on every request. The backend
 * (App\Http\Middleware\ResolveActiveStore) validates it against store_user
 * membership on every request and 403s if the caller doesn't actually have
 * access — so this header can never be spoofed into real access, it only
 * ever narrows/redirects what a legitimate multi-store user already has.
 *
 * Persisted to localStorage so a page refresh doesn't silently drop back to
 * the home store mid-session, and applied on module load (mirrors
 * core/config/api.js's own side-effect-on-import pattern) so the header is
 * set before the very first request goes out.
 */

const STORAGE_KEY = "activeStoreId";

export const getStoredActiveStoreId = () => {
  const raw = getStorageKey(STORAGE_KEY);
  const parsed = raw ? Number(raw) : null;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/** Sets (or clears, if falsy) the X-Active-Store header sent on every request. */
export const applyActiveStoreHeader = (storeId) => {
  if (storeId) {
    axios.defaults.headers.common["X-Active-Store"] = String(storeId);
  } else {
    delete axios.defaults.headers.common["X-Active-Store"];
  }
};

/** Persists the choice AND applies the header — the only two things a switch needs. */
export const persistActiveStoreId = (storeId) => {
  if (storeId) {
    setStorageKey(STORAGE_KEY, String(storeId));
  } else {
    removeStorageKey(STORAGE_KEY);
  }
  applyActiveStoreHeader(storeId);
};

/**
 * The store to adopt on load, from a GET /api/stores/accessible response — or
 * null to leave things as they are.
 *
 * "Adopt" means: this browser has never chosen a store (fresh login, new
 * device, cleared storage), and the user has a DEFAULT store
 * (users.default_store_id, flagged is_default by the API). The backend already
 * scopes the session to that same store on its own
 * (App\Http\Middleware\ResolveActiveStore), so adopting it here only brings the
 * UI into agreement — see UserContext's adoptDefaultStore().
 *
 * Returns null when a choice is already stored (an explicit switch always wins
 * over a default), when no entry is flagged, or when the flagged entry has no
 * real id — the old "Master Store" sentinel was flagged is_default with
 * `id: null`, and adopting THAT would persist a meaningless zero/NaN header.
 */
export const pickDefaultStoreId = (stores) => {
  if (getStoredActiveStoreId() !== null) {
    return null;
  }

  const match = (Array.isArray(stores) ? stores : []).find(
    (s) => s && s.is_default && s.id
  );

  if (!match) {
    return null;
  }

  const id = Number(match.id);

  return Number.isFinite(id) && id > 0 ? id : null;
};

export const clearActiveStore = () => {
  removeStorageKey(STORAGE_KEY);
  applyActiveStoreHeader(null);
};

// Re-apply on every module load (i.e. every page load/refresh) — without
// this, a switch made before a refresh would be persisted to localStorage
// but never actually sent on the next request until something else touched
// the header first.
applyActiveStoreHeader(getStoredActiveStoreId());
